// Gargantua — supermassive black hole system from Interstellar (2014).
//
// In-universe: a ~10^8 M☉ near-extremal Kerr black hole in a distant
// galaxy, reached through the Saturn-orbit wormhole. Three potentially
// habitable worlds orbit it — Miller's (ocean, extreme time dilation),
// Mann's (frozen ammonia clouds), and Edmunds' (terrestrial, the one
// the film ends on).
//
// Scene placement: parked far enough from Sol that none of its bodies
// can be seen against the Sol skybox under casual flying, but close
// enough that float32 transforms still resolve disk-scale detail
// without jitter. The wormhole transition handles the actual jump, so
// the "another galaxy" framing is satisfied by the tunnel flythrough
// rather than the literal scene-units distance.

/** @type {import('../schema.js').StarSystemData} */
export const gargantua = {
  id: 'gargantua',
  name: 'Gargantua',
  description: 'Supermassive black hole (~10^8 M☉) at the heart of a distant galaxy, reached through the Saturn-orbit wormhole. Three candidate worlds orbit close enough to its accretion disk that time dilation factors into mission planning. Inspired by Christopher Nolan\'s Interstellar (2014).',
  galaxyPosition: { x: 0, y: 0, z: 10_000_000 },     // marker: "another galaxy"
  spectralClasses: ['Supermassive BH'],
  distanceLY: 10_000_000,
  procedural: null,
  legacy: false,

  // Park well off the Sol vector so the lensing dome doesn't bleed
  // into the Sol skybox. The wormhole flythrough drives the actual
  // visit, so the in-scene distance is purely a "don't render this
  // when we're at Sol" hygiene choice.
  scenePosition: { x: 0, y: -8_000_000, z: 80_000_000 },

  stars: [
    {
      id: 'gargantua',
      name: 'Gargantua',
      type: 'BlackHole',
      spectralClass: 'Supermassive BH',
      radius: 72_000,                 // ~12× rsWorld; sets autopilot arrival distance
      temperature: 0,
      realDiameter: 'r_s ≈ 1.5 × 10⁹ km',
      description: 'A near-extremal Kerr black hole of about 100 million solar masses. The accretion disk glows from gas torn apart by tidal forces as it spirals to the event horizon; light from behind the hole is bent over the silhouette into a thin ring at the photon sphere. Time runs perceptibly slower for anything orbiting close.',
      isPrimary: true,
      position: { x: 0, y: 0, z: 0 },
      colors: ['#000000', '#ff9858'],
      glowColor: '#ff9858',
      gargantuaConfig: {
        rsWorld: 6_000,
        diskInner: 2.6,
        diskOuter: 12.0,
        exposure: 1.05
      }
    }
  ],

  planets: [
    {
      id: 'miller',
      name: "Miller's Planet",
      type: 'Ocean World',
      hostStarId: 'gargantua',
      // Tight orbit just outside the accretion disk's outer edge.
      // distance is in scene units; rsWorld=6000 means the disk outer
      // (12 r_s) sits at 72,000 units, so Miller at 110,000 is the
      // closest-in safe distance — still inside the gravity-well
      // crush zone that drives the canonical 1h = 7yr time dilation.
      radius: 4.5,
      distance: 110_000,
      rotationSpeed: 0.015,
      // Angular rate, like Sol's planets (Earth = 0.006). The earlier
      // 0.18 was tuned as if this were a ~100-unit orbit; at 110k units
      // it swept the planet ~19,800 scene-units/sec — a blur that no
      // camera follow could track. Sol-scale angular speeds keep the
      // background calm while the body still visibly orbits over minutes.
      orbitalSpeed: 0.006,
      eccentricity: 0.02,
      orbitalInclination: 0.04,
      tilt: 3,
      realDiameter: '~13,000 km',
      realDistance: '~1.5 × 10⁹ km from Gargantua',
      orbitalPeriod: '~1.7 hours (local)',
      moons: 0,
      description: 'A shallow ocean world orbiting deep in Gargantua\'s gravity well. The water covers the planet end to end at near-uniform depth; tidal bulges from the black hole drive kilometre-tall waves that sweep across the surface on ~1-hour cycles. Time dilation: one hour on the surface is roughly seven years for an observer near Mann\'s orbit.',
      hasAtmosphere: true,
      atmosphereScale: 1.020,
      atmosphereColor: 0xc8d0e0,
      atmosphereStrength: 0.45,
      rayleighTint: 0x6a8aae,
      rayleighStrength: 0.18,
      limbTint: 0xa8c0d8,
      colors: ['#2a4660', '#0a1828'],
      glowColor: '#6a8aae'
    },
    {
      id: 'mann',
      name: "Mann's Planet",
      type: 'Frozen World',
      hostStarId: 'gargantua',
      radius: 5.2,
      distance: 280_000,
      rotationSpeed: 0.018,
      // Sol-scale angular rate (see Miller's note). Outer of the inner
      // pair, so a touch slower than Miller.
      orbitalSpeed: 0.0025,
      eccentricity: 0.18,
      orbitalInclination: 0.12,
      tilt: 14,
      realDiameter: '~14,200 km',
      realDistance: '~3.8 × 10⁹ km from Gargantua',
      orbitalPeriod: '~2 months (local)',
      moons: 0,
      description: 'A frozen world of ammonia clouds and ice-pack canyons, far enough from Gargantua that time dilation is modest. The surface reads inviting from orbit — high albedo, breathable indications — but the clouds are frozen all the way to the ground and there is no exposed liquid water. The Lazarus probe that landed here reported habitable conditions that turned out to be a fabrication.',
      hasAtmosphere: true,
      atmosphereScale: 1.014,
      atmosphereColor: 0xeaf2ff,
      atmosphereStrength: 0.32,
      rayleighTint: 0x88a4c8,
      rayleighStrength: 0.10,
      limbTint: 0xccdcf0,
      colors: ['#d4e0ec', '#5a6e84'],
      glowColor: '#bccadc'
    },
    {
      id: 'edmunds',
      name: "Edmunds' Planet",
      type: 'Terrestrial Planet',
      hostStarId: 'gargantua',
      radius: 5.6,
      distance: 520_000,
      rotationSpeed: 0.012,
      // Sol-scale angular rate (see Miller's note). Outermost world, so
      // the slowest sweep of the three.
      orbitalSpeed: 0.0012,
      eccentricity: 0.08,
      orbitalInclination: 0.06,
      tilt: 22,
      realDiameter: '~14,800 km',
      realDistance: '~7.0 × 10⁹ km from Gargantua',
      orbitalPeriod: '~10 months (local)',
      moons: 0,
      description: 'A terrestrial world on the outer edge of Gargantua\'s habitable orbits — far enough from the hole that time dilation is comparable to Earth\'s. Edmunds\' early signals indicated a breathable atmosphere and surface water; the film closes on the implication that humanity\'s second cradle is established here. Renders with warm dusk light from the lensed disk.',
      hasAtmosphere: true,
      atmosphereScale: 1.018,
      atmosphereColor: 0xd4c098,
      atmosphereStrength: 0.42,
      rayleighTint: 0x886a44,
      rayleighStrength: 0.16,
      limbTint: 0xc8a878,
      colors: ['#8a6a48', '#2c2010'],
      glowColor: '#b8946a'
    }
  ],

  asteroidBelt: null,
  comets: [],

  summary: {
    starCount: 1,
    planetCount: 3,
    moonCount: 0,
    notableBodies: ["Gargantua", "Miller's", "Edmunds'"]
  }
};
